import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'

interface Consultation {
  id: string
  status: 'PENDING' | 'PAID' | 'EXPIRED'
  amount: number
  providerId: string
  companyId: string
  expiresAt: string | null
}

interface Message {
  id: string
  senderId: string
  senderRole: 'COMPANY' | 'PROVIDER'
  content: string
  createdAt: string
}

// Deux prestataires peuvent discuter ensemble (même rôle des deux côtés) : on se repère par identifiant
function idFromToken(): string | null {
  try {
    const token = localStorage.getItem('velink_token')
    if (!token) return null
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp * 1000 > Date.now() ? payload.sub : null
  } catch { return null }
}

function fmtEur(cents: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

export default function ConsultationChatPage() {
  const { id } = useParams<{ id: string }>()
  const myId = idFromToken()
  const [consultation, setConsultation] = useState<Consultation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      const c = await apiFetch<Consultation>(`/consultations/${id}`)
      setConsultation(c)
      if (c.status === 'PAID') {
        const msgs = await apiFetch<Message[]>(`/consultations/${id}/messages`)
        setMessages(msgs)
        apiFetch(`/consultations/${id}/messages/read`, { method: 'PATCH' }).catch(() => {})
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Consultation introuvable')
    }
  }, [id])

  useEffect(() => {
    load()
    const t = setInterval(load, 5000)
    return () => clearInterval(t)
  }, [load])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    const text = input.trim()
    if (!text) return
    setSending(true)
    setInput('')
    try {
      const msg = await apiFetch<Message>(`/consultations/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content: text }),
      })
      setMessages((prev) => [...prev, msg])
    } catch (e: unknown) {
      setInput(text)
      setError(e instanceof Error ? e.message : 'Envoi impossible')
    } finally {
      setSending(false)
    }
  }

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      <div className="glass-card p-8 text-center max-w-sm">
        <p className="text-danger text-sm mb-4">{error}</p>
        <Link to="/experts" className="glass-btn px-5 py-2.5 text-sm rounded-xl inline-block">Retour aux experts</Link>
      </div>
    </div>
  )

  if (!consultation) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
    </div>
  )

  if (consultation.status === 'PENDING') return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      <div className="glass-card p-8 text-center max-w-sm">
        <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-text-primary font-semibold mb-1">Paiement en cours de confirmation…</p>
        <p className="text-text-muted text-sm">Cette page se met à jour automatiquement.</p>
      </div>
    </div>
  )

  if (consultation.status === 'EXPIRED') return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      <div className="glass-card p-8 text-center max-w-sm">
        <p className="text-text-primary font-semibold mb-1">Cette consultation a expiré</p>
        <p className="text-text-muted text-sm mb-4">Le fil de discussion n'est plus accessible.</p>
        <Link to="/experts" className="glass-btn px-5 py-2.5 text-sm rounded-xl inline-block">Retour aux experts</Link>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #0A0A14 0%, #0D0D2B 50%, #0A0A14 100%)' }}>
      <header className="border-b border-white/[0.08] px-6 py-4 flex items-center justify-between flex-shrink-0">
        <Link to="/experts" className="text-text-secondary text-sm hover:text-text-primary transition-colors">← Retour</Link>
        <div className="text-center">
          <p className="text-text-primary text-sm font-semibold">Consultation payante</p>
          <p className="text-text-muted text-xs">{fmtEur(consultation.amount)} · {consultation.expiresAt ? `expire le ${new Date(consultation.expiresAt).toLocaleDateString('fr-FR')}` : 'sans limite de durée'}</p>
        </div>
        <span className="text-[11px] px-2 py-1 rounded-full bg-success/10 border border-success/25 text-success">Payée</span>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-6 max-w-2xl mx-auto w-full">
        {messages.length === 0 && (
          <p className="text-text-muted text-sm text-center mt-12">Aucun message. Démarrez la conversation.</p>
        )}
        <div className="space-y-3">
          {messages.map((m) => {
            const mine = m.senderId === myId
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${mine ? 'bg-accent text-white' : 'bg-white/[0.08] text-text-primary'}`}>
                  {m.content}
                </div>
              </div>
            )
          })}
        </div>
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="border-t border-white/[0.08] px-6 py-4 flex gap-3 flex-shrink-0">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Écrire un message…"
          className="glass-input flex-1 text-sm"
          disabled={sending}
        />
        <button type="submit" disabled={sending || !input.trim()} className="glass-btn-accent px-5 py-2.5 text-sm rounded-xl">
          Envoyer
        </button>
      </form>
    </div>
  )
}
